import { EmptyState, SearchIcon } from '@buffer-mono/popcorn'

import styles from './NoDataEmptyState.module.css'

import type { JSX } from 'react'

interface NoDataEmptyStateProps {
  description: string
  /** Shows "Try another date range." suggestion line */
  showSuggestion?: boolean
  /** If provided, appends "or clear the channel filter" link to the suggestion line */
  onClearChannelFilter?: () => void
  /** If provided, appends "or clear the tag filter" link to the suggestion line.
   * Used on the single-channel page, which filters by tag rather than channel.
   * Ignored when {@link onClearChannelFilter} is also set. */
  onClearTagFilter?: () => void
  /** If provided, appends "or clear the post type filter" link. Combines with
   * {@link onClearTagFilter} rather than losing to it. */
  onClearPostTypeFilter?: () => void
  /** Clears the tag and post type filters together, in one navigation. Used
   * only when both are active, where the link reads "clear the filters".
   *
   * The caller must clear both in a single URL update. Calling the two setters
   * above in turn does not work: each rebuilds the query from the snapshot it
   * parsed at render time, so the second restores the key the first removed. */
  onClearAllFilters?: () => void
  size?: 'small' | 'medium'
}

export function NoDataEmptyState({
  description,
  showSuggestion,
  onClearChannelFilter,
  onClearTagFilter,
  onClearPostTypeFilter,
  onClearAllFilters,
  size = 'small',
}: NoDataEmptyStateProps): JSX.Element {
  // The all-channels page filters by channel and the single-channel page by tag
  // and post type, so a channel clear never coexists with the other two — prefer
  // it if all are somehow passed. The label keeps the copy accurate to the
  // control, and clearing both at once when both are active avoids a dead end:
  // clearing only one of them would very often still show nothing.
  const bothSingleChannelFiltersActive =
    onClearTagFilter != null && onClearPostTypeFilter != null

  const clearFilter =
    onClearChannelFilter != null
      ? { label: 'channel filter', onClear: onClearChannelFilter }
      : bothSingleChannelFiltersActive && onClearAllFilters != null
        ? { label: 'filters', onClear: onClearAllFilters }
        : onClearTagFilter != null
          ? { label: 'tag filter', onClear: onClearTagFilter }
          : onClearPostTypeFilter != null
            ? { label: 'post type filter', onClear: onClearPostTypeFilter }
            : null
  const hasSuggestion = showSuggestion || clearFilter != null

  return (
    <EmptyState size={size} className={styles.emptyState}>
      <EmptyState.Icon>
        <SearchIcon stroke={1.5} />
      </EmptyState.Icon>
      {/* Description and suggestion share one paragraph so they read as a single
          sentence with normal line spacing, not two blocks with a large gap. */}
      <EmptyState.Description>
        {description}
        {hasSuggestion &&
          (clearFilter != null ? (
            <>
              {' '}
              Try another date range or{' '}
              <button
                type="button"
                className={styles.clearFilterLink}
                onClick={clearFilter.onClear}
              >
                clear the {clearFilter.label}
              </button>
              .
            </>
          ) : (
            ' Try another date range.'
          ))}
      </EmptyState.Description>
    </EmptyState>
  )
}
